import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import s from './ProjectEdit.module.css';
import { ReactComponent as DragIcon } from '../../../assets/icons/DragIcon.svg';
import { projectsApi } from '../../../api/projects';
import { api } from "../../../api";
import { ImageCropModal } from './ImageCropModal';

interface SubProject {
    id: number;
    title: string;
    description: string;
    image: string;
    tags: Record<string, string>;
    images: string[];
    sortOrder: number;
    imageSettings?: Record<number, { fit: string; position: string }>;
}

interface ProjectForm {
    title: string;
    image: string;
    status: 'published' | 'draft' | 'archived';
    column: number;
}

export const ProjectEdit: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const isNew = location.pathname.includes('/new');

    const queryParams = new URLSearchParams(location.search);
    const columnFromQuery = parseInt(queryParams.get('column') || '1');

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string>('');
    const [form, setForm] = useState<ProjectForm>({
        title: '',
        image: '',
        status: 'draft',
        column: columnFromQuery,
    });

    const [subProjects, setSubProjects] = useState<SubProject[]>([]);
    const [editingSubProject, setEditingSubProject] = useState<SubProject | null>(null);
    const [isAdding, setIsAdding] = useState(false);
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [newTagKey, setNewTagKey] = useState('');
    const [newTagValue, setNewTagValue] = useState('');
    const [draggedImageIndex, setDraggedImageIndex] = useState<number | null>(null);
    const [isDragOver, setIsDragOver] = useState(false);

    const [cropModalOpen, setCropModalOpen] = useState(false);
    const [croppingImageIndex, setCroppingImageIndex] = useState<number | null>(null);
    const [tempImageUrl, setTempImageUrl] = useState('');

    const fileInputRef = useRef<HTMLInputElement>(null);
    const dropAreaRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isNew && id) {
            loadProject(Number(id));
        } else {
            setSubProjects([]);
        }
    }, [id, isNew]);

    const loadProject = async (projectId: number) => {
        try {
            setLoading(true);
            const data = await projectsApi.getByIdAdmin(projectId);
            setForm({
                title: data.title,
                image: data.iconUrl || '',
                status: data.status,
                column: data.columnNumber,
            });
            setSubProjects(data.cards?.map(card => ({
                id: card.id,
                title: card.title,
                description: card.description || '',
                image: card.imageUrls?.[0] || '',
                tags: card.tags || {},
                images: card.imageUrls || [],
                sortOrder: card.sortOrder || 0,
                imageSettings: card.imageSettings || {},
            })) || []);
        } catch (error) {
            console.error('Ошибка загрузки проекта:', error);
            setError('Ошибка загрузки проекта');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setForm({
            ...form,
            [name]: value
        });
        if (error) setError('');
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                setForm({
                    ...form,
                    image: event.target?.result as string
                });
                if (error) setError('');
            };
            reader.readAsDataURL(file);
        }
    };

    const validateForm = (): boolean => {
        if (!form.title.trim()) {
            setError('Пожалуйста, заполните заголовок');
            return false;
        }
        if (!form.image) {
            setError('Пожалуйста, загрузите иконку');
            return false;
        }
        return true;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            setSaving(true);
            if (isNew) {
                const formData = new FormData();
                const projectData = {
                    title: form.title,
                    status: form.status,
                    columnNumber: form.column,
                };
                formData.append('project', new Blob([JSON.stringify(projectData)], {
                    type: 'application/json'
                }));

                if (form.image && form.image.startsWith('data:image')) {
                    const file = dataURLtoFile(form.image, 'icon.png');
                    formData.append('icon', file);
                }

                await api.post('/admin/projects', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            } else {
                if (!id) {
                    setError('Ошибка: ID проекта не найден');
                    return;
                }
                await projectsApi.update(Number(id), {
                    title: form.title,
                    status: form.status,
                    columnNumber: form.column,
                });
            }

            navigate('/admin/projects');
        } catch (error) {
            setError('Ошибка сохранения проекта');
        } finally {
            setSaving(false);
        }
    };

    function dataURLtoFile(dataurl: string, filename: string): File {
        const arr = dataurl.split(',');
        const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], filename, { type: mime });
    }

    const handleSubProjectChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (editingSubProject) {
            setEditingSubProject({
                ...editingSubProject,
                [e.target.name]: e.target.value
            });
        }
    };

    const handleImageSettingChange = (index: number, setting: 'fit' | 'position', value: string) => {
        if (!editingSubProject) return;

        const currentSettings = editingSubProject.imageSettings || {};
        const currentImageSetting = currentSettings[index] || { fit: 'cover', position: 'center center' };

        setEditingSubProject({
            ...editingSubProject,
            imageSettings: {
                ...currentSettings,
                [index]: {
                    ...currentImageSetting,
                    [setting]: value
                }
            }
        });
    };

    const handleSubProjectImageChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const file = e.target.files?.[0];
        if (file && editingSubProject) {
            const reader = new FileReader();
            reader.onload = (event) => {
                const newImages = [...editingSubProject.images];
                newImages[index] = event.target?.result as string;
                setEditingSubProject({
                    ...editingSubProject,
                    images: newImages
                });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        const files = e.dataTransfer.files;
        if (files.length === 0) return;

        if (e.currentTarget.id === 'projectImageDrop') {
            const file = files[0];
            if (file && file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onload = (event) => {
                    setForm({
                        ...form,
                        image: event.target?.result as string
                    });
                    if (error) setError('');
                };
                reader.readAsDataURL(file);
            }
            return;
        }

        if (editingSubProject) {
            const newImages = [...editingSubProject.images];
            let processedCount = 0;
            const totalFiles = files.length;

            Array.from(files).forEach((file, idx) => {
                if (file.type.startsWith('image/')) {
                    const reader = new FileReader();
                    reader.onload = (event) => {
                        const imageData = event.target?.result as string;

                        const emptyIndex = newImages.findIndex(img => img === '');
                        if (emptyIndex !== -1) {
                            newImages[emptyIndex] = imageData;
                        } else {
                            newImages.push(imageData);
                        }

                        processedCount++;
                        if (processedCount === totalFiles) {
                            setEditingSubProject({
                                ...editingSubProject,
                                images: newImages
                            });
                        }
                    };
                    reader.readAsDataURL(file);
                } else {
                    processedCount++;
                }
            });
        }
    };

    const handleImageDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        const files = e.dataTransfer.files;
        if (files.length === 0 || !editingSubProject) return;

        const newImages = [...editingSubProject.images];
        let processedCount = 0;
        const totalFiles = files.length;

        Array.from(files).forEach((file, idx) => {
            if (file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onload = (event) => {
                    const imageData = event.target?.result as string;

                    const emptyIndex = newImages.findIndex(img => img === '');
                    if (emptyIndex !== -1) {
                        newImages[emptyIndex] = imageData;
                    } else {
                        newImages.push(imageData);
                    }

                    processedCount++;
                    if (processedCount === totalFiles) {
                        setEditingSubProject({
                            ...editingSubProject,
                            images: newImages
                        });
                    }
                };
                reader.readAsDataURL(file);
            } else {
                processedCount++;
            }
        });
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    };

    const addSubProjectImage = () => {
        if (editingSubProject) {
            setEditingSubProject({
                ...editingSubProject,
                images: [...editingSubProject.images, '']
            });
        }
    };

    const removeSubProjectImage = (index: number) => {
        if (!editingSubProject) return;

        const imgToRemove = editingSubProject.images[index];

        if (imgToRemove && !imgToRemove.startsWith('data:image') && editingSubProject.id) {
            try {
                const urlParts = imgToRemove.split('/');
                const key = urlParts.slice(urlParts.indexOf('method-images') + 1).join('/');

                if (key) {
                    api.delete(`/admin/cards/${editingSubProject.id}/images?imageKey=${encodeURIComponent(key)}`)
                        .then(() => console.log('Фото удалено из S3:', key))
                        .catch(err => console.error('Ошибка удаления из S3:', err));
                }
            } catch (e) {
                console.error('Ошибка удаления:', e);
            }
        }

        const newImages = editingSubProject.images.filter((_, i) => i !== index);
        const newSettings = { ...editingSubProject.imageSettings };
        delete newSettings[index];
        setEditingSubProject({
            ...editingSubProject,
            images: newImages,
            imageSettings: newSettings
        });
    };

    const handleImageDragStart = (index: number) => {
        setDraggedImageIndex(index);
    };

    const handleImageDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        if (draggedImageIndex === null || draggedImageIndex === index) return;

        if (editingSubProject) {
            const newImages = [...editingSubProject.images];
            const [draggedItem] = newImages.splice(draggedImageIndex, 1);
            newImages.splice(index, 0, draggedItem);

            const newSettings = { ...editingSubProject.imageSettings };
            const draggedSetting = newSettings[draggedImageIndex];
            if (draggedSetting !== undefined) {
                delete newSettings[draggedImageIndex];
                const updatedSettings: Record<number, { fit: string; position: string }> = {};
                Object.keys(newSettings).forEach(key => {
                    const numKey = parseInt(key);
                    if (numKey >= index) {
                        updatedSettings[numKey + 1] = newSettings[numKey];
                    } else if (numKey === draggedImageIndex) {
                        updatedSettings[index] = newSettings[numKey];
                    } else {
                        updatedSettings[numKey] = newSettings[numKey];
                    }
                });

                setEditingSubProject({
                    ...editingSubProject,
                    images: newImages,
                    imageSettings: updatedSettings
                });
            } else {
                setEditingSubProject({
                    ...editingSubProject,
                    images: newImages
                });
            }
            setDraggedImageIndex(index);
        }
    };

    const handleImageDragEnd = () => {
        setDraggedImageIndex(null);
    };

    const handleCropImage = (index: number, imageUrl: string) => {
        setCroppingImageIndex(index);
        setTempImageUrl(imageUrl);
        setCropModalOpen(true);
    };

    const handleCropApply = (croppedImage: string) => {
        if (croppingImageIndex !== null && editingSubProject) {
            const newImages = [...editingSubProject.images];
            newImages[croppingImageIndex] = croppedImage;
            setEditingSubProject({
                ...editingSubProject,
                images: newImages
            });
        }
        setCropModalOpen(false);
        setCroppingImageIndex(null);
    };

    const addTag = () => {
        if (editingSubProject && newTagKey.trim() && newTagValue.trim()) {
            setEditingSubProject({
                ...editingSubProject,
                tags: {
                    ...editingSubProject.tags,
                    [newTagKey.trim()]: newTagValue.trim()
                }
            });
            setNewTagKey('');
            setNewTagValue('');
        }
    };

    const updateTag = (oldKey: string, newKey: string, newValue: string) => {
        if (editingSubProject && newKey.trim() && newValue.trim()) {
            const newTags = { ...editingSubProject.tags };
            delete newTags[oldKey];
            newTags[newKey.trim()] = newValue.trim();
            setEditingSubProject({
                ...editingSubProject,
                tags: newTags
            });
        }
    };

    const removeTag = (key: string) => {
        if (editingSubProject) {
            const newTags = { ...editingSubProject.tags };
            delete newTags[key];
            setEditingSubProject({
                ...editingSubProject,
                tags: newTags
            });
        }
    };

    const startEdit = (subProject: SubProject) => {
        setEditingSubProject({
            ...subProject,
            tags: { ...subProject.tags },
            images: [...subProject.images],
            sortOrder: subProject.sortOrder,
            imageSettings: subProject.imageSettings || {},
        });
        setIsAdding(false);
    };

    const startAdd = () => {
        setEditingSubProject({
            id: Date.now(),
            title: '',
            description: '',
            image: '',
            tags: {},
            images: [''],
            sortOrder: subProjects.length,
            imageSettings: {},
        });
        setIsAdding(true);
    };

    const saveSubProject = async () => {
        if (!editingSubProject) return;

        try {
            setSaving(true);

            const cardData = {
                projectId: Number(id),
                title: editingSubProject.title,
                description: editingSubProject.description,
                tags: editingSubProject.tags,
                imageKeys: editingSubProject.images.filter(img => !img.startsWith('data:image')),
                sortOrder: isAdding ? subProjects.length : editingSubProject.sortOrder,
                imageSettings: editingSubProject.imageSettings || {},
            };

            if (isAdding) {
                const formData = new FormData();
                formData.append('card', new Blob([JSON.stringify(cardData)], {
                    type: 'application/json'
                }));

                for (const img of editingSubProject.images) {
                    if (img && img.startsWith('data:image')) {
                        const file = dataURLtoFile(img, 'card_image.png');
                        formData.append('images', file);
                    }
                }

                const newCard = await api.post('/admin/cards', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                }).then(res => res.data);

                setSubProjects([...subProjects, {
                    ...editingSubProject,
                    id: newCard.id,
                    sortOrder: subProjects.length,
                    images: newCard.imageUrls || [],
                    imageSettings: newCard.imageSettings || {},
                }]);
            } else {
                const formData = new FormData();
                formData.append('card', new Blob([JSON.stringify(cardData)], {
                    type: 'application/json'
                }));

                for (const img of editingSubProject.images) {
                    if (img && img.startsWith('data:image')) {
                        const file = dataURLtoFile(img, 'card_image.png');
                        formData.append('images', file);
                    }
                }

                const updatedCard = await api.put(`/admin/cards/${editingSubProject.id}`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                }).then(res => res.data);

                setSubProjects(subProjects.map(p =>
                    p.id === editingSubProject.id ? {
                        ...editingSubProject,
                        images: updatedCard.imageUrls || [],
                        tags: updatedCard.tags || {},
                        imageSettings: updatedCard.imageSettings || {},
                    } : p
                ));
            }

            setEditingSubProject(null);
            setIsAdding(false);
            setNewTagKey('');
            setNewTagValue('');
        } catch (error) {
            console.error('Ошибка сохранения объекта:', error);
            alert('Ошибка при сохранении объекта');
        } finally {
            setSaving(false);
        }
    };

    const deleteSubProject = async (id: number) => {
        if (!window.confirm('Удалить объект?')) {
            return;
        }

        try {
            await api.delete(`/admin/cards/${id}`);
            setSubProjects(subProjects.filter(p => p.id !== id));
        } catch (error) {
            console.error('Ошибка удаления объекта:', error);
            alert('Ошибка при удалении объекта');
        }
    };

    const cancelEdit = () => {
        setEditingSubProject(null);
        setIsAdding(false);
        setNewTagKey('');
        setNewTagValue('');
    };

    const handleDragStart = (index: number) => {
        setDraggedIndex(index);
    };

    const handleDragOverSubProject = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === index) return;

        const newSubProjects = [...subProjects];
        const [draggedItem] = newSubProjects.splice(draggedIndex, 1);
        newSubProjects.splice(index, 0, draggedItem);

        newSubProjects.forEach((item, idx) => {
            item.sortOrder = idx;
        });

        setSubProjects(newSubProjects);
        setDraggedIndex(index);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
    };

    const handleImageSelect = (index: number) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = (e) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (file && editingSubProject) {
                const reader = new FileReader();
                reader.onload = (event) => {
                    const newImages = [...editingSubProject.images];
                    newImages[index] = event.target?.result as string;
                    setEditingSubProject({
                        ...editingSubProject,
                        images: newImages
                    });
                };
                reader.readAsDataURL(file);
            }
        };
        input.click();
    };

    if (loading) {
        return <div className={s.loading}>Загрузка...</div>;
    }

    return (
        <div className={s.editPage}>
            <button onClick={() => navigate('/admin/projects')} className={s.backBtn}>
                ← Назад
            </button>
            <div className={s.header}>
                <h1 className={s.title}>{isNew ? 'Создание направления' : 'Редактирование направления'}</h1>
                <button
                    className={s.saveBtn}
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? 'Сохранение...' : (isNew ? 'Создать' : 'Сохранить')}
                </button>
            </div>

            {error && (
                <div className={s.errorMessage}>
                    {error}
                </div>
            )}

            <div className={s.formGroup}>
                <label className={s.label}>Заголовок <span className={s.required}>*</span></label>
                <input
                    type="text"
                    name="title"
                    value={form.title}
                    onChange={handleChange}
                    className={`${s.input} ${error && !form.title.trim() ? s.inputError : ''}`}
                    placeholder="Введите название направления"
                />
            </div>

            <div className={s.formGroup}>
                <label className={s.label}>Статус</label>
                <select
                    name="status"
                    value={form.status}
                    onChange={handleChange}
                    className={s.select}
                >
                    <option value="draft">Черновик</option>
                    <option value="published">Опубликовано</option>
                    <option value="archived">Архив</option>
                </select>
            </div>

            <div className={s.editCard}>
                <div className={s.formGroup}>
                    <label className={s.label}>Иконка <span className={s.required}>*</span></label>
                    <div
                        id="projectImageDrop"
                        ref={dropAreaRef}
                        className={`${s.imageUpload} ${isDragOver ? s.dragOver : ''} ${error && !form.image ? s.imageUploadError : ''}`}
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onClick={() => document.getElementById('imageUpload')?.click()}
                    >
                        {form.image ? (
                            <img src={form.image} alt="Проект" className={s.previewImage} />
                        ) : (
                            <div className={s.emptyPreview}>
                                <span>Перетащите файл или нажмите для загрузки</span>
                            </div>
                        )}
                        <input
                            type="file"
                            accept="image/*"
                            className={s.fileInput}
                            onChange={handleImageChange}
                            id="imageUpload"
                        />
                        <label htmlFor="imageUpload" className={s.uploadBtn}>
                            {form.image ? 'Заменить иконку' : 'Загрузить иконку'}
                        </label>
                    </div>
                </div>
            </div>

            <div className={s.subProjectsSection}>
                <div className={s.subProjectsHeader}>
                    <h2 className={s.subProjectsTitle}>Объекты</h2>
                    <button
                        className={s.addSubProjectBtn}
                        onClick={startAdd}
                        disabled={saving}
                    >
                        + Добавить объект
                    </button>
                </div>

                {editingSubProject && (
                    <div className={s.subProjectEditForm}>
                        <h3>{isAdding ? 'Новый объект' : 'Редактирование объекта'}</h3>

                        <div className={s.formGroup}>
                            <label className={s.label}>Название</label>
                            <input
                                type="text"
                                name="title"
                                value={editingSubProject.title}
                                onChange={handleSubProjectChange}
                                className={s.input}
                                placeholder="Введите название"
                            />
                        </div>

                        <div className={s.formGroup}>
                            <label className={s.label}>Описание</label>
                            <textarea
                                name="description"
                                value={editingSubProject.description}
                                onChange={handleSubProjectChange}
                                className={s.textarea}
                                rows={3}
                                placeholder="Введите описание"
                            />
                        </div>

                        <div className={s.formGroup}>
                            <label className={s.label}>Особенности</label>
                            <div className={s.tagsContainer}>
                                {editingSubProject && Object.entries(editingSubProject.tags).map(([key, value]) => (
                                    <div key={key} className={s.tagItem}>
                                        <input
                                            type="text"
                                            value={key}
                                            onChange={(e) => {
                                                const newKey = e.target.value;
                                                if (newKey.trim() && newKey !== key) {
                                                    updateTag(key, newKey, value);
                                                }
                                            }}
                                            className={s.tagInputSmall}
                                            placeholder="Название"
                                        />
                                        <span className={s.tagSeparator}>:</span>
                                        <input
                                            type="text"
                                            value={value}
                                            onChange={(e) => {
                                                updateTag(key, key, e.target.value);
                                            }}
                                            className={s.tagInputSmall}
                                            placeholder="Значение"
                                        />
                                        <button
                                            className={s.removeTagBtn}
                                            onClick={() => removeTag(key)}
                                            type="button"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ))}

                                <div className={s.addTagRow}>
                                    <input
                                        type="text"
                                        placeholder="Название (город)"
                                        value={newTagKey}
                                        onChange={(e) => setNewTagKey(e.target.value)}
                                        className={s.tagInput}
                                    />
                                    <input
                                        type="text"
                                        placeholder="Значение (Хабаровск)"
                                        value={newTagValue}
                                        onChange={(e) => setNewTagValue(e.target.value)}
                                        className={s.tagInput}
                                    />
                                    <button
                                        className={s.addTagBtn}
                                        onClick={addTag}
                                        type="button"
                                        disabled={saving}
                                    >
                                        + Добавить
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className={s.formGroup}>
                            <label className={s.label}>Фотографии</label>
                            <div
                                className={`${s.imagesGrid} ${isDragOver ? s.dragOverImages : ''}`}
                                onDrop={handleImageDrop}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                            >
                                {editingSubProject.images.map((img, index) => {
                                    const settings = editingSubProject.imageSettings?.[index] || { fit: 'cover', position: 'center center' };

                                    return (
                                        <div key={index} className={s.imageItemWrapper}>
                                            <div
                                                className={`${s.imageItem} ${draggedImageIndex === index ? s.draggingImage : ''}`}
                                                draggable={!!img}
                                                onDragStart={() => img && handleImageDragStart(index)}
                                                onDragOver={(e) => img && handleImageDragOver(e, index)}
                                                onDragEnd={handleImageDragEnd}
                                                onClick={() => handleImageSelect(index)}
                                            >
                                                <div className={s.imageDragHandle}>
                                                    <DragIcon className={s.dragIconSmall} />
                                                </div>
                                                {img ? (
                                                    <>
                                                        <img
                                                            src={img}
                                                            alt={`Фото ${index + 1}`}
                                                            className={s.imagePreview}
                                                            style={{
                                                                objectFit: (settings.fit || 'cover') as 'cover' | 'contain' | 'fill' | 'scale-down' | 'none',
                                                                objectPosition: settings.position || 'center center',
                                                            }}
                                                        />
                                                        <div className={s.imageActions}>
                                                            <button
                                                                className={s.imageActionBtn}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleCropImage(index, img);
                                                                }}
                                                                type="button"
                                                                title="Обрезать фото"
                                                            >
                                                                ✂️
                                                            </button>
                                                            <button
                                                                className={`${s.imageActionBtn} ${s.removeImageBtn}`}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    removeSubProjectImage(index);
                                                                }}
                                                                type="button"
                                                                disabled={saving}
                                                                title="Удалить фото"
                                                            >
                                                                ✕
                                                            </button>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div className={s.emptyImagePreview}>
                                                        <span>Нажмите для загрузки</span>
                                                        <span className={s.emptyImageSubtext}>или перетащите файл</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                                <button
                                    className={s.addImageBtn}
                                    onClick={addSubProjectImage}
                                    type="button"
                                    disabled={saving}
                                >
                                    + Добавить фото
                                </button>
                            </div>
                        </div>

                        <div className={s.formActions}>
                            <button
                                className={s.saveSubProjectBtn}
                                onClick={saveSubProject}
                                disabled={saving}
                            >
                                {saving ? 'Сохранение...' : 'Сохранить'}
                            </button>
                            <button
                                className={s.cancelSubProjectBtn}
                                onClick={cancelEdit}
                                disabled={saving}
                            >
                                Отмена
                            </button>
                        </div>
                    </div>
                )}

                <div className={s.subProjectsList}>
                    {subProjects.length === 0 ? (
                        <div className={s.emptyCards}>
                            <p>Нет добавленных объектов</p>
                            <span>Нажмите "+ Добавить объект" чтобы создать первый</span>
                        </div>
                    ) : (
                        subProjects.map((sub, index) => (
                            <div
                                key={sub.id}
                                className={`${s.subProjectCard} ${draggedIndex === index ? s.dragging : ''}`}
                                draggable
                                onDragStart={() => handleDragStart(index)}
                                onDragOver={(e) => handleDragOverSubProject(e, index)}
                                onDragEnd={handleDragEnd}
                            >
                                <div className={s.cardNumber}>{index + 1}</div>
                                <div className={s.dragHandle}>
                                    <DragIcon className={s.dragIcon} />
                                </div>
                                <div className={s.subProjectCardContent}>
                                    <h4 className={s.subProjectCardTitle}>{sub.title || 'Без названия'}</h4>
                                    <p className={s.subProjectCardDesc}>{sub.description || 'Нет описания'}</p>
                                    <div className={s.subProjectCardDetails}>
                                        {Object.entries(sub.tags).slice(0, 3).map(([key, value]) => (
                                            <span key={key}>{key}: {value}</span>
                                        ))}
                                        {Object.keys(sub.tags).length > 3 && (
                                            <span>+{Object.keys(sub.tags).length - 3}</span>
                                        )}
                                    </div>
                                </div>
                                <div className={s.subProjectCardActions}>
                                    <button
                                        className={s.editSubProjectBtn}
                                        onClick={() => startEdit(sub)}
                                        disabled={saving}
                                    >
                                        ✏️
                                    </button>
                                    <button
                                        className={s.deleteSubProjectBtn}
                                        onClick={() => deleteSubProject(sub.id)}
                                        disabled={saving}
                                    >
                                        🗑️
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            <ImageCropModal
                isOpen={cropModalOpen}
                imageSrc={tempImageUrl}
                onClose={() => {
                    setCropModalOpen(false);
                    setCroppingImageIndex(null);
                }}
                onCrop={handleCropApply}
                aspectRatio={1}
            />
        </div>
    );
};